import i18n from 'i18n-js';
import * as types from './types';
import { cloneDeep } from 'lodash';
import { uiActions } from 'state/modules/UI';
import * as pricingActions from 'services/api/pricing';
import { getErrorMessage, parsePagination } from 'utils';
import { parseInputNumber, removeEmpty, isCancel } from 'utils';

// GET AD HOC TYPES
const fetchAdHocTypesRequest = () => ({
  type: types.FETCH_AD_HOC_TYPES_REQUEST,
});

const fetchAdHocTypesSuccess = (payload) => ({
  type: types.FETCH_AD_HOC_TYPES_SUCCESS,
  payload,
});

const fetchAdHocTypesFailed = ({ errors }) => ({
  type: types.FETCH_AD_HOC_TYPES_FAILED,
  errors,
});

export const fetchAdHocTypes = (params) => {
  return async (dispatch) => {
    dispatch(fetchAdHocTypesRequest());

    const response = await pricingActions.fetchAdhocTypes(params);

    if (response.ok) {
      return dispatch(fetchAdHocTypesSuccess(response.data));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchAdHocTypesFailed(response.data));
  };
};

// GET AD HOC TYPES BY ID
const fetchAdHocTypesByIdRequest = () => ({
  type: types.FETCH_AD_HOC_TYPES_BY_ID_REQUEST,
});

const fetchAdHocTypesByIdSuccess = (payload, list) => ({
  type: types.FETCH_AD_HOC_TYPES_BY_ID_SUCCESS,
  payload,
  list,
});

const fetchAdHocTypesByIdFailed = ({ errors }) => ({
  type: types.FETCH_AD_HOC_TYPES_BY_ID_FAILED,
  errors,
});

export const fetchAdHocTypesById = (params, list) => {
  return async (dispatch) => {
    dispatch(fetchAdHocTypesByIdRequest());

    const response = await pricingActions.fetchAdhocTypesById(params);

    if (response.ok) {
      return dispatch(fetchAdHocTypesByIdSuccess(response.data, list));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchAdHocTypesByIdFailed(response.data));
  };
};

const fetchPricingTypesRequest = () => ({
  type: types.FETCH_PRICING_TYPES_REQUEST,
});

const fetchPricingTypesSuccess = (payload) => ({
  type: types.FETCH_PRICING_TYPES_SUCCESS,
  payload,
});

const fetchPricingTypesFailed = (errors) => ({
  type: types.FETCH_PRICING_TYPES_FAILED,
  errors,
});

export const fetchPricingTypes = (params) => {
  return async (dispatch) => {
    dispatch(fetchPricingTypesRequest());

    const response = await pricingActions.fetchPricingTypes(params);

    if (response.ok) {
      return dispatch(fetchPricingTypesSuccess(response.data));
    }
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchPricingTypesFailed(response.data));
  };
};

// Reset Adhoc types

export const resetAdhocTypes = () => {
  return async (dispatch) => {
    dispatch({
      type: types.RESET_AD_HOC_TYPES,
    });
  };
};

// GET RULESETS
const fetchRulesetsRequest = () => ({
  type: types.FETCH_RULESETS_REQUEST,
});

const fetchRulesetsSuccess = (payload) => ({
  type: types.FETCH_RULESETS_SUCCESS,
  payload,
});

const fetchRulesetsFailed = (errors) => ({
  type: types.FETCH_RULESETS_FAILED,
  errors,
});

export const fetchRulesets = (params, options) => {
  return async (dispatch) => {
    dispatch(fetchRulesetsRequest());

    const response = await pricingActions.fetchRulesets(params, options);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchRulesetsSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    if (!isCancel(response)) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: getErrorMessage(response.data),
        })
      );
      dispatch(fetchRulesetsFailed(response.data));
    }
  };
};

// GET TAX RULESETS
const fetchTaxRulesetsRequest = () => ({
  type: types.FETCH_TAX_RULESETS_REQUEST,
});

const fetchTaxRulesetsSuccess = (payload) => ({
  type: types.FETCH_TAX_RULESETS_SUCCESS,
  payload,
});

const fetchTaxRulesetsFailed = (payload) => ({
  type: types.FETCH_TAX_RULESETS_FAILED,
  payload,
});

export const fetchTaxRulesets = (params, options) => {
  return async (dispatch) => {
    dispatch(fetchTaxRulesetsRequest());

    const response = await pricingActions.fetchTaxRulesets(params, options);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchTaxRulesetsSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    if (isCancel(response)) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: getErrorMessage(response.data),
        })
      );
      dispatch(fetchTaxRulesetsFailed(response.data));
    }
  };
};

// GET RULESETS
const fetchRulesetRequest = () => ({
  type: types.FETCH_RULESET_REQUEST,
});

const fetchRulesetSuccess = (payload) => ({
  type: types.FETCH_RULESET_SUCCESS,
  payload,
});

const fetchRulesetFailed = (errors) => ({
  type: types.FETCH_RULESET_FAILED,
  errors,
});

export const fetchRuleset = (id) => {
  return async (dispatch) => {
    dispatch(fetchRulesetRequest());

    const response = await pricingActions.fetchRuleset(id);

    if (response.ok) {
      const data = { ...response.data };
      return dispatch(fetchRulesetSuccess(data));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );

    dispatch(fetchRulesetFailed(response.data));
  };
};

const fetchRuleSuccess = (payload) => ({
  type: types.FETCH_RULE_SUCCESS,
  payload,
});

export const fetchRule = (id, type) => {
  return async (dispatch, getState) => {
    const { current } = getState().pricing;

    const data = current?.rules && current?.rules[type] ? current?.rules[type][id - 1] || {} : {};

    data.all_time = data?.start === 1 && data?.end === 8 ? true : false;

    dispatch(fetchRuleSuccess(data));
  };
};

const fetchRemoveRuleSuccess = (payload) => ({
  type: types.FETCH_REMOVE_RULE_SUCCESS,
  payload,
});

export const fetchRemoveRule = (index) => {
  return async (dispatch) => {
    dispatch(
      fetchRemoveRuleSuccess({
        index,
      })
    );
  };
};

const fetchUpdateRulesSuccess = (payload) => ({
  type: types.FETCH_UPDATE_RULES_SUCCESS,
  payload,
});

export const fetchUpdateRules = (rules) => {
  return async (dispatch) => {
    dispatch(
      fetchUpdateRulesSuccess({
        rules,
      })
    );
  };
};

const fetchUpdateRuleByIdSuccess = (payload) => ({
  type: types.FETCH_UPDATE_RULE_BY_ID_SUCCESS,
  payload,
});

export const fetchUpdateRuleById = (id, data) => {
  return async (dispatch) => {
    dispatch(
      fetchUpdateRuleByIdSuccess({
        rule: data,
        index: id,
      })
    );
  };
};

const fetchUpdateRulesetInfoSuccess = (payload) => ({
  type: types.FETCH_UPDATE_RULESET_INFO_SUCCESS,
  payload,
});

export const fetchUpdateRulesetInfo = (data) => {
  return async (dispatch) => {
    dispatch(fetchUpdateRulesetInfoSuccess(data));
  };
};

// FETCH DELETE RULESET
const fetchDeleteRulesetRequest = () => ({
  type: types.FETCH_DELETE_RULESET_REQUEST,
});

const fetchDeleteRulesetSuccess = () => ({
  type: types.FETCH_DELETE_RULESET_SUCCESS,
});

const fetchDeleteRulesetFailed = (errors) => ({
  type: types.FETCH_DELETE_RULESET_FAILED,
  errors,
});

export const fetchDeleteRuleset = (id, payload, onSuccess = {}) => {
  return async (dispatch) => {
    dispatch(fetchDeleteRulesetRequest());

    const response = await pricingActions.fetchDeleteRuleset(id, payload);

    if (response.ok) {
      if (onSuccess?.action) {
        onSuccess.action(onSuccess.params);
      }
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('ruleset.updated_ruleset_msg'),
        })
      );
      return dispatch(fetchDeleteRulesetSuccess());
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchDeleteRulesetFailed(response.data));
  };
};

const fetchAccountingTypesRequest = () => ({
  type: types.FETCH_ACCOUNTING_TYPES_REQUEST,
});

const fetchAccountingTypesSuccess = (payload) => ({
  type: types.FETCH_ACCOUNTING_TYPES_SUCCESS,
  payload,
});

const fetchAccountingTypesFailed = (errors) => ({
  type: types.FETCH_ACCOUNTING_TYPES_FAILED,
  errors,
});

export const fetchAccountingTypes = (params) => {
  return async (dispatch) => {
    dispatch(fetchAccountingTypesRequest());

    const response = await pricingActions.fetchAccountingTypes(params);

    if (response.ok) {
      return dispatch(fetchAccountingTypesSuccess(response.data));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchAccountingTypesFailed(response.data));
  };
};

// Add ruleset
const fetchAddRulesetRequest = () => ({
  type: types.FETCH_ADD_RULESET_REQUEST,
});

const fetchAddRulesetSuccess = (payload) => ({
  type: types.FETCH_ADD_RULESET_SUCCESS,
  payload,
});

export const fetchAddRuleset = (payload) => {
  return async (dispatch) => {
    dispatch(fetchAddRulesetRequest());

    let data = { ...payload };

    // remove empty fields
    data = removeEmpty(data);

    if (!data.start) {
      data.start = 1;
    }

    if (!data.end) {
      data.end = 8;
    }

    if (data.unit_price) {
      data.unit_price = parseInputNumber(data.unit_price);
    }

    if (!data.description) {
      data.description = '';
    }

    dispatch(fetchAddRulesetSuccess(data));
  };
};

// ADD TAX TO TAX RULESET
const fetchAddTaxToRulesetRequest = () => ({
  type: types.FETCH_ADD_TAX_RULESET_REQUEST,
});

const fetchAddTaxToRulesetSuccess = (payload) => ({
  type: types.FETCH_ADD_TAX_RULESET_SUCCESS,
  payload,
});

export const fetchAddTaxToRuleset = (payload, handleClick) => {
  return async (dispatch) => {
    dispatch(fetchAddTaxToRulesetRequest());

    const data = cloneDeep(payload);

    dispatch(fetchAddTaxToRulesetSuccess(data));
    if (handleClick) handleClick();
  };
};

// REMOVE TAX FROM TAX RULESET
const fetchRemoveTaxRuleSuccess = (payload) => ({
  type: types.FETCH_REMOVE_TAXES,
  payload,
});

export const fetchRemoveTaxRule = (index) => {
  return async (dispatch, getState) => {
    const { currentTaxRuleset } = getState().pricing;
    let data = currentTaxRuleset?.taxes ? currentTaxRuleset?.taxes : [];
    data.splice(index, 1);
    dispatch(fetchRemoveTaxRuleSuccess(data));
  };
};

// UPDATE TAX FROM TAX RULESET
const fetchUpdateTaxesSuccess = (payload) => ({
  type: types.FETCH_UPDATE_TAXES,
  payload,
});

export const fetchUpdateTaxes = (index, tax, handleClick) => {
  return async (dispatch, getState) => {
    const { currentTaxRuleset } = getState().pricing;
    let data = currentTaxRuleset.taxes || [];
    data[index] = tax;
    dispatch(fetchUpdateTaxesSuccess(data));

    if (handleClick) handleClick();
  };
};

// UPDATE TAXES ORDER
const fetchUpdateTaxesOrderSuccess = (payload) => ({
  type: types.FETCH_UPDATE_TAXES_ORDER,
  payload,
});

export const fetchUpdateTaxesOrder = (data) => {
  return async (dispatch) => {
    dispatch(fetchUpdateTaxesOrderSuccess(data));
  };
};

// UPDATE TAXRULESET INFO
const fetchUpdateTaxRulesetInfoRequest = (payload) => ({
  type: types.FETCH_UPDATE_TAX_RULESET_INFO,
  payload,
});

export const fetchUpdateTaxRulesetInfo = (data) => {
  return async (dispatch) => {
    dispatch(fetchUpdateTaxRulesetInfoRequest(data));
  };
};

// RESET TAXRULESET INFO
const fetchResetTaxRulesetRequest = () => ({
  type: types.FETCH_RESET_TAX_RULESET,
});

export const fetchResetTaxRuleset = (data) => {
  return async (dispatch) => {
    dispatch(fetchResetTaxRulesetRequest());
  };
};

// CREATE TAX RULESET
const fetchCreateTaxRulesetRequest = () => ({
  type: types.FETCH_CREATE_TAX_RULESET_REQUEST,
});

const fetchCreateTaxRulesetSuccess = (payload) => ({
  type: types.FETCH_CREATE_TAX_RULESET_SUCCESS,
  payload,
});

const fetchCreateTaxRulesetFailed = (errors) => ({
  type: types.FETCH_CREATE_TAX_RULESET_FAILED,
  errors,
});

export const fetchCreateTaxRuleset = (params) => {
  return async (dispatch) => {
    dispatch(fetchCreateTaxRulesetRequest());

    const _params = cloneDeep(params);
    let payload = {
      name: params.name,
      description: params.description || undefined,
    };

    // remove unneeded fields
    payload.tax_rates = _params?.taxes?.map((val) => {
      delete val.location;
      return val;
    });

    const response = await pricingActions.fetchCreateTaxRuleset(payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('tax_ruleset.created'),
        })
      );
      return dispatch(
        fetchCreateTaxRulesetSuccess({
          data: response.data,
        })
      );
    }

    const formattedError = getErrorMessage(response?.data);

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError[0].message,
      })
    );

    dispatch(fetchCreateTaxRulesetFailed(formattedError));
  };
};

// UPDATE TAX RULESET
const fetchUpdateTaxRulesetRequest = () => ({
  type: types.FETCH_UPDATE_TAX_RULESET_REQUEST,
});

const fetchUpdateTaxRulesetSuccess = (payload) => ({
  type: types.FETCH_UPDATE_TAX_RULESET_SUCCESS,
  payload,
});

const fetchUpdateTaxRulesetFailed = (errors) => ({
  type: types.FETCH_UPDATE_TAX_RULESET_FAILED,
  errors,
});

export const fetchUpdateTaxRuleset = (id, params) => {
  return async (dispatch) => {
    dispatch(fetchUpdateTaxRulesetRequest());

    const _params = cloneDeep(params);
    let payload = {
      name: params.name,
      description: params.description || undefined,
    };

    // remove unneeded fields
    payload.tax_rates = _params?.taxes?.map((val) => {
      delete val.location;
      delete val.display_name;
      return val;
    });

    const response = await pricingActions.fetchUpdateTaxRuleset(id, payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('tax_ruleset.updated'),
        })
      );
      return dispatch(
        fetchUpdateTaxRulesetSuccess({
          data: response.data,
        })
      );
    }

    const formattedError = getErrorMessage(response?.data);

    dispatch(fetchUpdateTaxRulesetFailed(response.data));

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError[0].message,
      })
    );
  };
};

// GET TAX RULESET BY ID
const fetchTaxRulesetByIdRequest = () => ({
  type: types.FETCH_TAX_RULESET_BY_ID_REQUEST,
});

const fetchTaxRulesetByIdSuccess = (payload) => ({
  type: types.FETCH_TAX_RULESET_BY_ID_SUCCESS,
  payload,
});

const fetchTaxRulesetByIdFailed = (errors) => ({
  type: types.FETCH_TAX_RULESET_BY_ID_FAILED,
  errors,
});

export const fetchTaxRulesetById = (params) => {
  return async (dispatch) => {
    dispatch(fetchTaxRulesetByIdRequest());

    const response = await pricingActions.fetchTaxRulesetById(params);

    if (response.ok) {
      let data = { ...response.data };

      data.tax_rates = data?.tax_rates?.map((value) => {
        return {
          ...value,
          location_id: value?.location?.id,
        };
      });
      return dispatch(fetchTaxRulesetByIdSuccess(data));
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchTaxRulesetByIdFailed(response.data));
  };
};

// FETCH DELETE TAX RULESET
const fetchDeleteTaxRulesetRequest = () => ({
  type: types.FETCH_DELETE_TAX_RULESET_REQUEST,
});

const fetchDeleteTaxRulesetSuccess = () => ({
  type: types.FETCH_DELETE_TAX_RULESET_SUCCESS,
});

const fetchDeleteTaxRulesetFailed = (errors) => ({
  type: types.FETCH_DELETE_TAX_RULESET_FAILED,
  errors,
});

export const fetchDeleteTaxRuleset = (id, onSuccess = {}) => {
  return async (dispatch) => {
    dispatch(fetchDeleteTaxRulesetRequest());

    const response = await pricingActions.fetchDeleteTaxRuleset(id);

    if (response.ok) {
      if (onSuccess?.action) {
        onSuccess.action(onSuccess.params);
      }
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('tax_ruleset.deleted'),
        })
      );
      return dispatch(fetchDeleteTaxRulesetSuccess());
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchDeleteTaxRulesetFailed(response.data));
  };
};

// Reset current tax ruleset
export const resetCurrentTaxRuleset = () => (dispatch) => {
  return dispatch({ type: types.RESET_CURRENT_TAX_RULESET });
};

// Onboarding
const onExit = (payload) => ({
  type: types.SET_EXIT,
  payload,
});

export const setExit = (exit, type) => (dispatch) => {
  return dispatch(onExit({ exit, type }));
};

const onNextPosition = (payload) => ({
  type: types.CHANGE_POSITION,
  payload,
});

export const changePosition = (type, exit) => (dispatch, getState) => {
  if (exit) {
    return dispatch(setExit(exit, 'exitOnboarding'));
  }

  const {
    onboarding: { position },
  } = getState().skus;

  const nextPosition = type === 'next' ? 1 : -1;

  return dispatch(onNextPosition({ position: position + nextPosition }));
};

export const changeStep = (step) => (dispatch, getState) => {
  const {
    onboarding: { position },
  } = getState().skus;

  let nextPosition;

  switch (step) {
    case 'sku':
      nextPosition = 0;
      break;
    case 'config':
      nextPosition = 1;
      break;
    default:
      nextPosition = position;
  }

  return dispatch(onNextPosition({ position: position + nextPosition }));
};

// Reset onboarding
export const resetOnboarding = () => (dispatch) => {
  return dispatch({ type: types.RESET_ONBOARDING });
};

// Reset current ruleset
export const resetCurrentRuleset = () => (dispatch) => {
  return dispatch({ type: types.RESET_CURRENT_RULESET });
};

// CREATE RULESET
const fetchCreateRulesetRequest = () => ({
  type: types.FETCH_CREATE_RULESET_REQUEST,
});

const fetchCreateRulesetSuccess = (payload) => ({
  type: types.FETCH_CREATE_RULESET_SUCCESS,
  payload,
});

const fetchCreateRulesetFailed = ({ errors }) => ({
  type: types.FETCH_CREATE_RULESET_FAILED,
  errors,
});

export const fetchCreateRuleset = (params) => {
  return async (dispatch) => {
    dispatch(fetchCreateRulesetRequest());

    let data = cloneDeep(params);

    // remove empty fields
    data.rules = data.rules?.map((val) => {
      if (val?.invoice_item_type?.id) {
        val.invoice_item_type_id = val?.invoice_item_type?.id;
      }
      if (!val.description) {
        val.description = '';
      }
      let objCleaned = removeEmpty(val, ['description'], ['invoice_item_type']);
      return objCleaned;
    });

    const response = await pricingActions.fetchCreateRuleset(data);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('ruleset.created'),
        })
      );
      return dispatch(
        fetchCreateRulesetSuccess({
          data: response.data,
        })
      );
    }

    const formattedError = getErrorMessage(response?.data);

    dispatch(fetchCreateRulesetFailed(response.data));

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError[0].message,
      })
    );
  };
};

// UPDATE RULESET
const fetchUpdateRulesetRequest = (data) => ({
  type: types.FETCH_UPDATE_RULESET_REQUEST,
  data,
});

const fetchUpdateRulesetSuccess = (payload) => ({
  type: types.FETCH_UPDATE_RULESET_SUCCESS,
  payload,
});

const fetchUpdateRulesetFailed = ({ errors }) => ({
  type: types.FETCH_UPDATE_RULESET_FAILED,
  errors,
});

export const fetchUpdateRuleset = (id, data) => {
  return async (dispatch) => {
    dispatch(fetchUpdateRulesetRequest());

    const payload = { ...data };

    // remove empty fields
    payload.rules = payload?.rules?.map((val) => {
      if (val?.invoice_item_type?.id) {
        val.invoice_item_type_id = val?.invoice_item_type?.id;
      }
      let objCleaned = removeEmpty(val, ['description'], ['pricing_type']);
      return objCleaned;
    });

    const response = await pricingActions.fetchUpdateRuleset(id, payload);

    if (response.ok) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('ruleset.updated'),
        })
      );
      return dispatch(
        fetchUpdateRulesetSuccess({
          data: response.data,
        })
      );
    }

    const formattedError = getErrorMessage(response?.data);

    dispatch(fetchUpdateRulesetFailed(formattedError));

    if (typeof formattedError === 'string') {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError[0].message,
      })
    );
  };
};

// FETCH RULESET VG's
const fetchRulesetVehicleGroupsRequest = (data) => ({
  type: types.FETCH_RULESET_VEHICLE_GROUPS_REQUEST,
  data,
});

const fetchRulesetVehicleGroupsSuccess = (payload) => ({
  type: types.FETCH_RULESET_VEHICLE_GROUPS_SUCCESS,
  payload,
});

const fetchRulesetVehicleGroupsFailed = ({ errors }) => ({
  type: types.FETCH_RULESET_VEHICLE_GROUPS_FAILED,
  errors,
});

export const fetchRulesetVehicleGroups = (id, params) => {
  return async (dispatch) => {
    dispatch(fetchRulesetVehicleGroupsRequest());
    const response = await pricingActions.fetchRulesetVehicleGroups(id, params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchRulesetVehicleGroupsSuccess({
          data: response.data,
          pagination,
        })
      );
    }
    const error = getErrorMessage(response.data);
    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: error,
      })
    );
    dispatch(fetchRulesetVehicleGroupsFailed(error));
  };
};

// GET SKUs ASSOCIATED TO TAX RULESET
const fetchSKUsAssociatedRequest = (data) => ({
  type: types.FETCH_SKUS_ASSOCIATED_REQUEST,
  data,
});

const fetchSKUsAssociatedSuccess = (payload) => ({
  type: types.FETCH_SKUS_ASSOCIATED_SUCCESS,
  payload,
});

const fetchSKUsAssociatedFailed = ({ errors }) => ({
  type: types.FETCH_SKUS_ASSOCIATED_FAILED,
  errors,
});

export const fetchSKUsAssociated = (id, params) => {
  return async (dispatch) => {
    dispatch(fetchSKUsAssociatedRequest());

    const response = await pricingActions.fetchSKUsAssociated(id, params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchSKUsAssociatedSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    const formattedError = getErrorMessage(response?.data);

    dispatch(fetchSKUsAssociatedFailed(formattedError));

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: formattedError,
      })
    );
  };
};
